import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, User, Phone } from 'lucide-react';
import { toast } from 'sonner';
import DateInput from './DateInput';
import PhoneInput from './PhoneInput';

export default function EditPatientModal({ open, onClose, onSuccess, patient, healthUnits = [] }) {
  const [nome, setNome] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [unitId, setUnitId] = useState('');
  const [telefone, setTelefone] = useState(''); // Novo campo
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (patient) {
      setNome(patient.nome || '');
      setDataNascimento(patient.data_nascimento || '');
      setUnitId(patient.unit_id || 'none');
      setTelefone(patient.telefone || ''); // Carregar telefone existente
    }
  }, [patient]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nome.trim()) {
      toast.error('O nome do paciente é obrigatório');
      return;
    }

    // Validar telefone se preenchido
    if (telefone && telefone.length !== 15) {
      toast.error('Telefone deve estar no formato (DD) 99999-9999');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.put(`/patients/${patient.id}`, {
        nome: nome.trim(),
        data_nascimento: dataNascimento || '01/01/1970',
        unit_id: unitId === 'none' ? null : unitId,
        telefone: telefone || null  // Enviar telefone
      });

      toast.success('Dados do paciente atualizados com sucesso!');
      onSuccess();
    } catch (error) {
      console.error('Erro ao atualizar paciente:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao atualizar paciente';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getUnitName = () => {
    if (!unitId || unitId === 'none') return 'Nenhuma unidade';
    const unit = healthUnits.find(u => u.id === unitId);
    return unit ? unit.nome : 'Unidade não encontrada';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <User className="w-6 h-6 text-primary" />
            Editar Paciente
          </DialogTitle>
          <DialogDescription>
            Atualize as informações cadastrais do paciente
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Nome */}
          <div>
            <Label htmlFor="nome" className="text-sm font-medium mb-2 block">
              Nome do Paciente *
            </Label>
            <Input
              id="nome"
              data-testid="edit-patient-name-input"
              value={nome}
              onChange={(e) => setNome(e.target.value.toUpperCase())}
              placeholder="NOME COMPLETO"
              className="bg-gray-50 border-transparent focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12"
              required
            />
          </div>

          {/* Data de Nascimento */}
          <div>
            <Label htmlFor="dataNascimento" className="text-sm font-medium mb-2 block">
              Data de Nascimento
            </Label>
            <DateInput
              id="dataNascimento"
              data-testid="edit-patient-birth-date-input"
              value={dataNascimento}
              onChange={setDataNascimento}
              className="bg-gray-50 border-transparent focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12"
            />
          </div>

          {/* Unidade de Saúde */}
          <div>
            <Label htmlFor="unit" className="text-sm font-medium mb-2 block">
              Unidade de Saúde
            </Label>
            <Select value={unitId || 'none'} onValueChange={setUnitId}>
              <SelectTrigger 
                className="bg-gray-50 border-transparent focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12"
                data-testid="edit-patient-unit-select"
              >
                <SelectValue placeholder="Selecione a unidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma unidade</SelectItem>
                {healthUnits.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Telefone com máscara */}
          <div>
            <Label htmlFor="telefone" className="text-sm font-medium mb-2 block">
              <Phone className="w-4 h-4 inline mr-1" />
              Telefone (WhatsApp)
            </Label>
            <PhoneInput
              id="telefone"
              data-testid="edit-patient-phone-input"
              value={telefone}
              onChange={setTelefone}
              className="bg-gray-50 border-transparent focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12"
            />
            <p className="text-xs text-gray-500 mt-1">
              Formato: (DD) 99999-9999 - Para receber lembretes de renovação
            </p>
          </div>

          {/* Informações Atuais */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Dados Atuais:</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Nome:</span> {patient?.nome}</p>
              <p><span className="font-medium">Nascimento:</span> {patient?.data_nascimento || 'Não informado'}</p>
              <p><span className="font-medium">Telefone:</span> {patient?.telefone || 'Não informado'}</p>
              <p><span className="font-medium">Unidade:</span> {getUnitName()}</p>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-lg px-6"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6"
              data-testid="save-patient-button"
            >
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
